import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '../lib/cn.js'
import { Icone } from './Icone.js'

export type TomAviso = 'sucesso' | 'erro' | 'alerta' | 'info'
type Aviso = { id: number; tom: TomAviso; texto: string; detalhe?: string | undefined }

const ICONE = { sucesso: CheckCircle2, erro: XCircle, alerta: AlertTriangle, info: Info }

const Ctx = createContext<{ avisar: (a: Omit<Aviso, 'id'>) => void } | null>(null)

export function useAviso() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAviso precisa estar dentro de <ProvedorAvisos>')
  return ctx
}

export function ProvedorAvisos({ children }: { children: React.ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])
  // Só o setter é usado: o id sai de dentro do updater, que lê o valor
  // anterior. Guardar o número também geraria um re-render por aviso sem
  // ninguém ler o resultado.
  const [, setSeq] = useState(0)

  const avisar = useCallback(
    (a: Omit<Aviso, 'id'>) => {
      setSeq((s) => {
        const id = s + 1
        setAvisos((atuais) => [...atuais, { ...a, id }])
        // Erro não some sozinho: quem errou precisa ler com calma.
        if (a.tom !== 'erro') {
          setTimeout(() => setAvisos((atuais) => atuais.filter((x) => x.id !== id)), 5000)
        }
        return id
      })
    },
    [],
  )

  const valor = useMemo(() => ({ avisar }), [avisar])

  return (
    <Ctx.Provider value={valor}>
      {children}
      {/* aria-live polite: anuncia sem interromper o que a pessoa está fazendo */}
      <div className="du-avisos" role="status" aria-live="polite">
        {avisos.map((a) => (
          <div key={a.id} className={cn('du-aviso', `du-aviso--${a.tom}`)}>
            <Icone de={ICONE[a.tom]} tamanho={20} />
            <span className="du-aviso__corpo">
              <strong>{a.texto}</strong>
              {a.detalhe ? <span>{a.detalhe}</span> : null}
            </span>
            <button
              type="button"
              className="du-aviso__fechar"
              onClick={() => setAvisos((atuais) => atuais.filter((x) => x.id !== a.id))}
              aria-label="Dispensar aviso"
            >
              <Icone de={X} tamanho={16} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}
